// Module 9 — Counterparties & Compliance.
// Route: /reinsurance/counterparties
//
// Reinsurer onboarding, ratings and compliance status.

import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ModulePage from './ModulePage';
import CounterpartyDrawer from './CounterpartyDrawer';
import {
  C, Icons, Kpi, Pill, PrimaryButton, GhostButton, date, humanise,
} from './kit';

// "A+ / AA-" — AM Best over S&P, the pairing the design's RATING column uses.
// A party with neither shows an em dash rather than an empty cell, so an
// unrated counterparty is visibly unrated instead of looking like a gap.
function ratingPair(r) {
  const of = (agency) => (r.ratings || [])
    .filter(x => x.agency === agency && x.ratedAt)
    .sort((a, b) => new Date(b.ratedAt) - new Date(a.ratedAt))[0]?.rating;
  const am = of('AM Best');
  const sp = of('S&P');
  if (!am && !sp) return <span style={{ color: C.faint }}>— / —</span>;
  return <span style={{ fontWeight: 600, color: C.ink }}>{am || '—'} / {sp || '—'}</span>;
}

// The single compliance answer for the list: the worst of KYC and sanctions.
// Reporting them separately in one column would need two pills; reporting
// only sanctions would call a party with expired KYC "Clear".
function compliancePosition(r) {
  if (['flagged', 'blocked'].includes(r.sanctions?.status)) return 'flagged';
  if (r.kycExpired || r.kyc?.status === 'expired') return 'expired';
  if (r.kyc?.status === 'failed') return 'flagged';
  if (r.kyc?.status === 'in_progress' || r.capacity?.status === 'under_review') return 'under_review';
  if (r.kyc?.status === 'clear' && r.sanctions?.status === 'clear') return 'clear';
  return 'under_review';
}

// Onboarding a counterparty. Compliance starts at pending, never clear: a
// party is cleared by someone doing the check, not by someone typing the
// answer into the form that creates them.
const counterpartyForm = (t) => ({
  title: t('screens.reCounterparties.newTitle', 'New counterparty'),
  subtitle: t('screens.reCounterparties.newSub', 'Add a party we contract with'),
  submitLabel: t('screens.reCounterparties.createBtn', 'Add counterparty'),
  endpoint: '/reinsurance/counterparties',
  fields: [
    { key: 'name', label: t('screens.reCounterparties.fNameIn', 'Legal name'), required: true,
      placeholder: 'Hannover Re' },
    { key: 'type', label: t('screens.reCounterparties.fTypeIn', 'Type'), type: 'select', required: true,
      defaultValue: 'reinsurer',
      options: [
        { value: 'reinsurer', label: 'Reinsurer' },
        { value: 'cedant', label: 'Cedant' },
        { value: 'broker', label: 'Broker' },
        { value: 'retrocessionaire', label: 'Retrocessionaire' },
      ] },
    { key: 'country', label: t('screens.reCounterparties.fCountryIn', 'Domicile'), required: true,
      placeholder: 'DE',
      help: t('screens.reCounterparties.fCountryHelp', 'Two-letter country code.') },
    { key: 'ownerName', label: t('screens.reCounterparties.fOwnerIn', 'Relationship owner'),
      placeholder: 'Hisham Al Hashimi' },
    { key: 'typicalCapacity', label: t('screens.reCounterparties.fCapacityIn', 'Typical capacity'),
      type: 'number', placeholder: '250000000' },
    { key: 'currency', label: t('screens.reCounterparties.fCurrencyIn', 'Currency'), defaultValue: 'USD' },
  ],
  toPayload: (v) => ({
    name: v.name.trim(),
    type: v.type,
    country: v.country.trim().toUpperCase(),
    ...(v.ownerName ? { ownerName: v.ownerName.trim() } : {}),
    status: 'active',
    relationshipSince: new Date().toISOString(),
    capacity: {
      status: 'open',
      ...(v.typicalCapacity !== '' ? { typicalCapacity: Number(v.typicalCapacity) } : {}),
      currency: (v.currency || 'USD').trim().toUpperCase(),
    },
    // Unchecked, and saying so. A new party that appeared already cleared
    // would be a compliance record nobody produced.
    kyc: { status: 'pending' },
    sanctions: { status: 'pending' },
  }),
});

export default function CounterpartiesList() {
  const history = useHistory();
  const { t } = useTranslation();

  const columns = [
    { key: 'name', label: t('screens.reCounterparties.colName', 'Counterparty'),
      render: (r) => <span style={{ fontWeight: 600, color: C.ink }}>{r.name}</span> },
    { key: 'type', label: t('screens.reCounterparties.colType', 'Type'), width: 104,
      render: (r) => humanise(r.type) },
    { key: 'country', label: t('screens.reCounterparties.colDomicile', 'Domicile'), width: 120,
      render: (r) => r.country || '—' },
    { key: 'rating', label: t('screens.reCounterparties.colRating', 'Rating AM Best / S&P'), width: 128,
      render: ratingPair },
    { key: 'lines', label: t('screens.reCounterparties.colLines', 'Lines supported'),
      render: (r) => {
        const l = r.authorizedLinesOfBusiness || [];
        if (!l.length) return <span style={{ color: C.faint }}>—</span>;
        return <span style={{ fontSize: 12.5 }}>{l.slice(0, 3).join(', ')}{l.length > 3 ? ` +${l.length - 3}` : ''}</span>;
      } },
    { key: 'capacity.status', label: t('screens.reCounterparties.colCapacity', 'Capacity status'), width: 116,
      // `closed` here means the market is shut to us, which is not the same
      // "closed" as a finished file — see STATUS_TONE.
      render: (r) => {
        const s = r.capacity?.status || 'open';
        return <Pill status={s} tone={s === 'closed' ? 'neutral' : undefined} />;
      } },
    { key: 'compliance', label: t('screens.reCounterparties.colCompliance', 'Compliance status'), width: 128,
      sortable: false,
      render: (r) => <Pill status={compliancePosition(r)} /> },
    // KYC and sanctions were rolled into one "Compliance status" pill, so the
    // list could not say WHICH of the two was outstanding — and a counterparty
    // failing sanctions looked the same as one whose KYC had merely expired.
    // Those are not the same conversation.
    { key: 'kyc.status', label: t('screens.reCounterparties.colKyc', 'KYC'), width: 104,
      render: (r) => <Pill status={r.kyc?.status || 'not_started'} size='sm' /> },
    { key: 'sanctions.status', label: t('screens.reCounterparties.colSanctions', 'Sanctions'), width: 112,
      render: (r) => <Pill status={r.sanctions?.status || 'not_screened'} size='sm' /> },
    { key: 'lastReviewAt', label: t('screens.reCounterparties.colReview', 'Last review'), width: 108,
      render: (r) => date(r.lastReviewAt) },
    { key: 'ownerName', label: t('screens.reCounterparties.colOwner', 'Owner'),
      render: (r) => r.ownerName || <span style={{ color: C.faint }}>unassigned</span> },
  ];

  return (
    <ModulePage
      title={t('screens.reCounterparties.title', 'Counterparties & Compliance')}
      subtitle={t('screens.reCounterparties.subtitle', 'Reinsurer onboarding, ratings and compliance status')}
      endpoint='/reinsurance/counterparties'
      searchPlaceholder={t('screens.reCounterparties.search', 'Search counterparties, country, rating or reference…')}
      createForm={counterpartyForm(t)}
      actions={({ openCreate }) => (
        <>
          <GhostButton onClick={() => history.push('/reinsurance/compliance')}>
            {t('screens.reCounterparties.runScreening', 'Run screening')}
          </GhostButton>
          <PrimaryButton onClick={openCreate}>{t('screens.reCounterparties.new', 'Add counterparty')}</PrimaryButton>
        </>
      )}
      renderKpis={(s) => (
        <>
          <Kpi icon={Icons.users()} tone='blue'
            value={s?.activeCounterparties?.value ?? '—'} trend={s?.activeCounterparties?.trend}
            comparison={s?.activeCounterparties?.comparison}
            label={t('screens.reCounterparties.kpiActive', 'Active counterparties')} />
          <Kpi icon={Icons.clock()} tone='amber'
            value={s?.pendingReviews?.value ?? '—'} trend={s?.pendingReviews?.trend}
            comparison={s?.pendingReviews?.comparison}
            label={t('screens.reCounterparties.kpiPending', 'Pending reviews')} />
          <Kpi icon={Icons.alert()} tone='red'
            value={s?.sanctionAlerts?.value ?? '—'} trend={s?.sanctionAlerts?.trend}
            comparison={s?.sanctionAlerts?.comparison}
            label={t('screens.reCounterparties.kpiSanctions', 'Sanction alerts')} />
          <Kpi icon={Icons.doc()} tone='neutral'
            value={s?.expiringDocuments?.value ?? '—'} trend={s?.expiringDocuments?.trend}
            comparison={s?.expiringDocuments?.comparison}
            label={t('screens.reCounterparties.kpiDocs', 'Expiring documents')} />
        </>
      )}
      filters={[
        { key: 'type', label: 'Type', options: [
          { value: 'all', label: 'All types' },
          { value: 'reinsurer', label: 'Reinsurers' },
          { value: 'broker', label: 'Brokers' },
          { value: 'cedant', label: 'Cedants' },
          { value: 'retrocessionaire', label: 'Retrocessionaires' },
        ] },
        { key: 'kycStatus', label: 'KYC', options: [
          { value: 'all', label: 'All KYC statuses' },
          { value: 'clear', label: 'Clear' },
          { value: 'in_progress', label: 'In progress' },
          { value: 'expired', label: 'Expired' },
          { value: 'failed', label: 'Failed' },
          { value: 'not_started', label: 'Not started' },
        ] },
        { key: 'sanctionsStatus', label: 'Sanctions', options: [
          { value: 'all', label: 'All sanctions statuses' },
          { value: 'clear', label: 'Clear' },
          { value: 'flagged', label: 'Flagged' },
          { value: 'blocked', label: 'Blocked' },
          { value: 'not_screened', label: 'Not screened' },
        ] },
      ]}
      columns={columns}
      emptyMessage={t('screens.reCounterparties.empty', 'No counterparties registered yet.')}
      drawerTabs={['Overview', 'Compliance', 'Ratings', 'Treaties']}
      drawerHeader={(r) => ({
        title: r.name,
        badge: <Pill status={r.type === 'reinsurer' ? 'in_review' : 'draft'} label={humanise(r.type)} />,
        lines: [
          r.legalName && r.legalName !== r.name ? r.legalName : null,
          [r.country, humanise(r.type), humanise(r.status)].filter(Boolean).join(' · '),
        ].filter(Boolean),
      })}
      drawer={(r, { tab, reload }) => (
        <CounterpartyDrawer row={r} tab={tab} reload={reload} />
      )}
    />
  );
}
